// Geteilte Bausteine der Startseiten-Varianten: DOM-Helfer, Formatierung,
// Datenzugriff. Bewusst ohne Layout-Annahmen – jede Variante rendert selbst.

export {
  contextualDocumentTitle,
  isTrustedVorgang,
  VORGANG_LIMIT,
} from "./search-helpers";

export const S3_BASE = "https://hel1.your-objectstorage.com/ratsinfocux";

export function el(tag, props = {}, children = []) {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(props)) {
    if (v == null || v === false) continue;
    if (k === "class") node.className = v;
    else if (k === "text") node.textContent = v;
    else if (k.startsWith("on") && typeof v === "function")
      node.addEventListener(k.slice(2).toLowerCase(), v);
    else node.setAttribute(k, v);
  }
  for (const child of Array.isArray(children) ? children : [children]) {
    if (child == null || child === false) continue;
    node.appendChild(
      typeof child === "string" ? document.createTextNode(child) : child
    );
  }
  return node;
}

/**
 * Tatsächlich in S3 abgelegte Dateiendung.
 *
 * Maßgeblich ist `archive_ext` aus dem Index — sie lässt sich NICHT aus `link`
 * ableiten: Der Indexer konvertiert Anhänge nach PDF; klappt das, liegt die Datei
 * als .pdf, sonst mit ihrer Original-Endung (.docx, .xlsx, .jpg …). Für Dokumente,
 * die vor Einführung des Feldes indexiert wurden, greift die alte Heuristik
 * (Tagesordnungspunkte = .txt, sonst .pdf).
 */
export function archiveExt(doc) {
  const ext = typeof doc.archive_ext === "string" ? doc.archive_ext.trim() : "";
  if (ext) return ext.startsWith(".") ? ext : `.${ext}`;
  return isTextDocument(doc) ? ".txt" : ".pdf";
}

/** Textbeiträge (Tagesordnungspunkte) liegen als .txt und haben nie ein Vorschaubild. */
export function isTextDocument(doc) {
  if (typeof doc.archive_ext === "string" && doc.archive_ext.trim()) {
    return doc.archive_ext.trim().toLowerCase().endsWith("txt");
  }
  return typeof doc.link === "string" && doc.link.includes("to020");
}

/** Nur aus PDFs erzeugt der Indexer ein Vorschaubild. */
export function hasThumbnail(doc) {
  return archiveExt(doc).toLowerCase() === ".pdf";
}

/** Direktlink auf die archivierte Originaldatei in S3. */
export function docUrl(doc) {
  return `${S3_BASE}/${doc.hash}${archiveExt(doc)}`;
}

/**
 * Link auf die Detailseite eines Dokuments. Listen und Treffer verlinken hierher
 * statt direkt auf die Datei — die Seite zeigt zusätzlich die KI-Anreicherungen
 * (Zusammenfassung, Themengruppen, Schlagwörter, Abstimmungen) und führt von dort
 * weiter zum Original.
 */
export function documentUrl(doc, query = "") {
  const hash = typeof doc === "string" ? doc : doc?.hash;
  const base = `/dokument?hash=${encodeURIComponent(hash ?? "")}`;
  // Suchbegriff mitgeben, damit die Detailseite ihn im Dokument hervorhebt.
  return query.trim() ? `${base}&q=${encodeURIComponent(query.trim())}` : base;
}

/** Kurzes Label für die Dateiart, z.B. "PDF", "DOCX", "Text". */
export function fileLabel(doc) {
  const ext = archiveExt(doc).replace(/^\./, "").toUpperCase();
  return ext === "TXT" ? "Text" : ext;
}

export function thumbUrl(doc) {
  return `${S3_BASE}/${doc.hash}.png`;
}

/** Platzhalter, wenn kein Vorschaubild existiert — hält das Raster gleichmäßig. */
function previewPlaceholder(doc, cls) {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  for (const [k, v] of Object.entries({
    width: "22",
    height: "22",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    "stroke-width": "1.6",
    "stroke-linecap": "round",
    "stroke-linejoin": "round",
    "aria-hidden": "true",
  })) {
    svg.setAttribute(k, v);
  }
  for (const [tag, attrs] of [
    [
      "path",
      { d: "M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" },
    ],
    ["path", { d: "M14 2v5h6" }],
    ["path", { d: "M8 13h8" }],
    ["path", { d: "M8 17h5" }],
  ]) {
    const node = document.createElementNS("http://www.w3.org/2000/svg", tag);
    for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
    svg.appendChild(node);
  }
  return el("div", { class: `preview-fallback ${cls}`.trim() }, [
    svg,
    el("span", {
      class: "preview-fallback-label",
      text: doc.dokumenttyp || fileLabel(doc),
    }),
  ]);
}

/**
 * Vorschau eines Dokuments. Nicht jedes Dokument hat ein Thumbnail: Textbeiträge
 * haben grundsätzlich keins, und auch bei PDFs kann das Bild fehlen. In beiden
 * Fällen wird ein Platzhalter gezeigt statt einer leeren Fläche.
 */
export function preview(doc, { imgClass = "", fallbackClass = "" } = {}) {
  // Nur PDFs haben überhaupt ein Vorschaubild – Textbeiträge und nicht
  // konvertierte Anhänge (.docx, .xlsx, …) bekommen direkt den Platzhalter.
  if (!hasThumbnail(doc)) return previewPlaceholder(doc, fallbackClass);

  const img = el("img", {
    class: imgClass,
    src: thumbUrl(doc),
    alt: "",
    loading: "lazy",
  });
  img.addEventListener("error", () => {
    img.replaceWith(previewPlaceholder(doc, fallbackClass));
  });
  return img;
}

export function formatDate(value) {
  if (!value) return "";
  const d =
    typeof value === "number" ? new Date(value * 1000) : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatDateTime(value, hasTime = true) {
  if (!value) return "";
  const d =
    typeof value === "number" ? new Date(value * 1000) : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const date = d.toLocaleDateString("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
  if (!hasTime) return `${date} · Uhrzeit noch offen`;
  return (
    date +
    ", " +
    d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) +
    " Uhr"
  );
}

export function relativeDay(value) {
  if (!value) return "";
  const d =
    typeof value === "number" ? new Date(value * 1000) : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const days = Math.round((d.getTime() - Date.now()) / 86400000);
  if (days === 0) return "heute";
  if (days === 1) return "morgen";
  if (days === -1) return "gestern";
  if (days > 1) return `in ${days} Tagen`;
  return `vor ${Math.abs(days)} Tagen`;
}

export async function fetchHome({ documents = 8, meetings = 5 } = {}) {
  const res = await fetch(
    `/api/home?documents=${documents}&meetings=${meetings}`
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function searchPreview(query, limit = 5) {
  const res = await fetch(
    `/api/search-preview?q=${encodeURIComponent(query)}&limit=${limit}`
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/** Themen-Vorschläge für Chips/Beispielsuchen. */
export const TOPICS = [
  "Radweg",
  "Bebauungsplan",
  "Kita",
  "Haushalt",
  "Windkraft",
  "Feuerwehr",
  "Tourismus",
  "Schule",
];
